import { useEffect, useRef, type RefObject } from "react";

import { useI18n } from "../i18n.js";

/**
 * I due grafici della scheda ANALYTICS, portati da `StackedEmissionChart.qml`
 * e `WindEmissionScatter.qml`.
 *
 * Stanno insieme perche' condividono la tavolozza — un impianto deve avere lo
 * STESSO colore nei due grafici e nelle pastiglie del filtro, altrimenti
 * leggere l'uno accanto all'altro diventa un esercizio di memoria.
 */

/** Sei tonalita' distinguibili anche su fondo scuro, nell'ordine del `.qml`. */
export const CLUSTER_COLORS = [
  "hsl(210, 90%, 55%)",
  "hsl(142, 71%, 45%)",
  "hsl(48, 96%, 53%)",
  "hsl(0, 84%, 60%)",
  "hsl(280, 65%, 60%)",
  "hsl(30, 90%, 55%)",
];

export function clusterColor(i: number): string {
  return CLUSTER_COLORS[i % CLUSTER_COLORS.length]!;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatPeriod(p: string): string {
  if (!p) return "";
  if (p.length === 7) return MONTHS[Number(p.slice(5, 7)) - 1] ?? p;
  if (p.length === 10) return `${p.slice(8, 10)}/${p.slice(5, 7)}`;
  if (p.length >= 13 && p.includes("T")) return `${p.slice(11, 13)}:00`;
  return p;
}

function formatMassShort(kg: number): string {
  if (kg >= 1) return `${kg.toFixed(1)} kg`;
  const g = kg * 1000;
  if (g >= 1) return `${g.toFixed(0)} g`;
  return `${(g * 1000).toFixed(0)} mg`;
}

/** Ridisegna su una tela adattata al riquadro, a ogni cambio di dati o
 *  dimensione. */
function useCanvas(
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
  deps: unknown[],
): RefObject<HTMLCanvasElement | null> {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const render = () => {
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w <= 0 || h <= 0) return;
      const dpr = globalThis.devicePixelRatio || 1;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      drawRef.current(ctx, w, h);
    };
    render();
    const ro = new ResizeObserver(render);
    ro.observe(parent);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

export interface StackedData {
  periods: string[];
  clusters: { clusterId: string; clusterName: string; values: number[] }[];
}

/**
 * Le emissioni nel tempo, impilate per impianto.
 *
 * Impilate e non affiancate: la domanda che si fa guardandolo e' "quanto ha
 * emesso il sito in quel periodo", e la risposta e' l'altezza della colonna.
 * Chi ha contribuito e quanto e' il secondo sguardo, ed e' il colore a dirlo.
 */
export function StackedEmissionChart({ data }: { data: StackedData | null }) {
  const { t } = useI18n();
  const periods = data?.periods ?? [];
  const clusters = data?.clusters ?? [];

  const ref = useCanvas(
    (ctx, width, height) => {
      const n = periods.length;
      const nc = clusters.length;
      if (n === 0) return;

      const labelH = 28;
      const labelW = 52;
      const plotX = labelW;
      const plotY = 8;
      const plotW = width - labelW - 8;
      const plotH = height - labelH - 8;

      // Il massimo e' la SOMMA per periodo: prendere il massimo dei singoli
      // schiaccerebbe le colonne fuori dal riquadro.
      let maxVal = 1e-9;
      for (let i = 0; i < n; i++) {
        let s = 0;
        for (const c of clusters) s += Number(c.values[i]) || 0;
        maxVal = Math.max(maxVal, s);
      }
      if (maxVal <= 0) maxVal = 1;

      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      for (let g = 1; g <= 4; g++) {
        const gy = plotY + plotH - (g / 4) * plotH;
        ctx.beginPath();
        ctx.moveTo(plotX, gy);
        ctx.lineTo(plotX + plotW, gy);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      const slotW = plotW / n;
      const barW = Math.max(2, Math.min(40, slotW * 0.65));

      for (let i = 0; i < n; i++) {
        const bx = plotX + i * slotW + (slotW - barW) / 2;
        let baseY = plotY + plotH;

        for (let c = 0; c < nc; c++) {
          const val = Number(clusters[c]!.values[i]) || 0;
          if (val <= 0) continue;
          const segH = Math.max(1, (val / maxVal) * plotH);
          const segY = baseY - segH;
          ctx.fillStyle = clusterColor(c);

          // Solo il segmento in cima ha gli angoli smussati: smussarli tutti
          // farebbe sembrare la colonna spezzata.
          let isTop = true;
          for (let cc = c + 1; cc < nc; cc++) {
            if ((Number(clusters[cc]!.values[i]) || 0) > 0) {
              isTop = false;
              break;
            }
          }
          if (isTop) {
            const r = 2;
            ctx.beginPath();
            ctx.moveTo(bx + r, segY);
            ctx.lineTo(bx + barW - r, segY);
            ctx.arcTo(bx + barW, segY, bx + barW, segY + r, r);
            ctx.lineTo(bx + barW, segY + segH);
            ctx.lineTo(bx, segY + segH);
            ctx.lineTo(bx, segY + r);
            ctx.arcTo(bx, segY, bx + r, segY, r);
            ctx.closePath();
            ctx.fill();
          } else {
            ctx.fillRect(bx, segY, barW, segH);
          }
          baseY = segY;
        }

        ctx.fillStyle = "rgba(180,180,180,0.7)";
        ctx.font = "9px monospace";
        ctx.textAlign = "center";
        ctx.fillText(formatPeriod(periods[i] ?? ""), bx + barW / 2, plotY + plotH + 18);
      }

      ctx.textAlign = "right";
      ctx.fillStyle = "rgba(180,180,180,0.65)";
      ctx.font = "9px monospace";
      for (let k = 0; k <= 4; k++) {
        const v = (k / 4) * maxVal;
        const ky = plotY + plotH - (k / 4) * plotH;
        ctx.fillText(formatMassShort(v), plotX - 4, ky + 4);
      }
    },
    [data],
  );

  return (
    <section className="achart">
      <h3>{t("analytics.emissionsByCluster")}</h3>
      <div className="achart__box">
        {clusters.length > 0 && (
          <div className="achart__legend">
            {clusters.map((c, i) => (
              <span key={c.clusterId}>
                <i style={{ background: clusterColor(i) }} />
                {c.clusterName}
              </span>
            ))}
          </div>
        )}
        <div className="achart__canvas">
          <canvas ref={ref} />
          {periods.length === 0 && <span className="achart__empty">{t("emissions.noData")}</span>}
        </div>
      </div>
    </section>
  );
}

export interface WindPoint {
  clusterId: string;
  windSpeed: number;
  emissionRate: number;
}

/**
 * Vento contro portata, un punto per lettura.
 *
 * Serve a capire se le emissioni misurate seguono il vento — cioe' se la
 * misura sta guardando una perdita o un artefatto della dispersione. Le
 * letture senza uno dei due valori sono gia' escluse dal server: un punto con
 * il vento assente non dice niente sulla correlazione e sporcherebbe il
 * grafico.
 */
export function WindEmissionScatter({
  points,
  clusterNames,
}: {
  points: WindPoint[];
  clusterNames: Record<string, string>;
}) {
  const { t } = useI18n();

  const grouped: { clusterId: string; name: string; points: WindPoint[]; colorIdx: number }[] = [];
  const index = new Map<string, number>();
  for (const p of points) {
    let i = index.get(p.clusterId);
    if (i === undefined) {
      i = grouped.length;
      index.set(p.clusterId, i);
      grouped.push({
        clusterId: p.clusterId,
        name: clusterNames[p.clusterId] ?? p.clusterId,
        points: [],
        colorIdx: i,
      });
    }
    grouped[i]!.points.push(p);
  }

  const ref = useCanvas(
    (ctx, width, height) => {
      if (grouped.length === 0) return;
      const labelH = 22;
      const labelW = 52;
      const plotX = labelW;
      const plotY = 8;
      const plotW = width - labelW - 8;
      const plotH = height - labelH - 8;

      let minX = Infinity;
      let maxX = -Infinity;
      let maxY = 0;
      for (const g of grouped) {
        for (const p of g.points) {
          const x = Number(p.windSpeed) || 0;
          const y = Number(p.emissionRate) || 0;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
      if (minX === Infinity) return;
      if (maxX <= minX) maxX = minX + 1;
      if (maxY <= 0) maxY = 1;
      const xRange = maxX - minX || 1;
      const xMin = Math.max(0, minX - xRange * 0.05);
      const xMax = maxX + xRange * 0.05;

      const toX = (v: number) => plotX + ((v - xMin) / (xMax - xMin)) * plotW;
      const toY = (v: number) => plotY + plotH - (v / maxY) * plotH;

      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      for (let g = 1; g <= 4; g++) {
        const gy = plotY + plotH - (g / 4) * plotH;
        ctx.beginPath();
        ctx.moveTo(plotX, gy);
        ctx.lineTo(plotX + plotW, gy);
        ctx.stroke();
        const gx = plotX + (g / 4) * plotW;
        ctx.beginPath();
        ctx.moveTo(gx, plotY);
        ctx.lineTo(gx, plotY + plotH);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      ctx.globalAlpha = 0.7;
      for (const g of grouped) {
        ctx.fillStyle = clusterColor(g.colorIdx);
        for (const p of g.points) {
          ctx.beginPath();
          ctx.arc(toX(Number(p.windSpeed) || 0), toY(Number(p.emissionRate) || 0), 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      ctx.textAlign = "right";
      ctx.fillStyle = "rgba(180,180,180,0.65)";
      ctx.font = "9px monospace";
      for (let k = 0; k <= 4; k++) {
        const v = (k / 4) * maxY;
        const ky = plotY + plotH - (k / 4) * plotH;
        ctx.fillText(v.toFixed(1), plotX - 4, ky + 4);
      }
      ctx.textAlign = "center";
      for (let k = 0; k <= 4; k++) {
        const v = xMin + (k / 4) * (xMax - xMin);
        ctx.fillText(v.toFixed(1), plotX + (k / 4) * plotW, plotY + plotH + 14);
      }
    },
    [points, clusterNames],
  );

  return (
    <section className="achart">
      <h3>{t("analytics.windVsEmissions")}</h3>
      <div className="achart__box">
        {grouped.length > 1 && (
          <div className="achart__legend">
            {grouped.map((g) => (
              <span key={g.clusterId}>
                <i style={{ background: clusterColor(g.colorIdx) }} />
                {g.name}
              </span>
            ))}
          </div>
        )}
        <div className="achart__canvas">
          <canvas ref={ref} />
          {points.length === 0 && <span className="achart__empty">{t("emissions.noData")}</span>}
        </div>
        {points.length > 0 && (
          <div className="achart__xlabel">{t("analytics.windSpeed")} (m/s)</div>
        )}
      </div>
    </section>
  );
}
